import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { Observable } from 'rxjs';
import { map, distinct } from 'rxjs/operators';

const LOCAL_STORAGE_KEY = 'vwd-local-data';

@Injectable()
export class LocalDataService {
  constructor(private readonly localStorage?: LocalStorageService) {
    if (!localStorage) {
      this.localStorage = new LocalStorageService();
    }
  }

  public get(key: string): any {
    return this.getRoot()[key];
  }

  public set(key: string, value: any): any {
    const root = this.getRoot();
    root[key] = value;
    this.localStorage.set(LOCAL_STORAGE_KEY, root);
  }

  /**
   * Returns an observable stream that pushes every change to the specified
   * key, including the initial value.
   *
   * Usage:
   *
   * localData.watch('mykey', { defaultValue: new Array<string>() }).subscribe(array => {
   *   ...
   * });
   *
   * @param key The key to watch
   * @param options Additional options:
   *        - defaultValue: The value to use if the key is not set
   */
  public watch<T = any>(key: string, options?: { defaultValue?: T; }): Observable<T> {
    const defaultValue = options && 'defaultValue' in options ? options.defaultValue : null;
    return this.localStorage.watch(LOCAL_STORAGE_KEY, { defaultValue: {} })
    .pipe(
      map(root => key in root ? <T> root[key] : defaultValue),
      distinct(list => JSON.stringify(list))
    );
  }

  private getRoot() {
    return this.localStorage.get(LOCAL_STORAGE_KEY) || {};
  }
}
